<template>
  <base-card class="relative overflow-visible !px-0">
    <base-card-body>
      <div class="flex flex-row justify-between items-start gap-4">
        <div class="flex flex-1">
          <secure-prescription-pages
            :urls="prescription.files"
            :size="106"
          />
        </div>
        <div class="flex flex-1 flex-col justify-between gap-3">
          <div class="flex flex-col items-start gap-3">
            <div class="flex flex-row gap-2 items-center">
              <div class="flex flex-col">
                <div
                  class="font-bold min-w-64 hover:opacity-50 cursor-pointer"
                  @click="$router.push(`/customer/${prescription.customer.id}/prescriptions`)"
                >
                  {{
                    prescription.beneficiary?.information.name || prescription.customer?.information.name || 'Anonyme'
                  }}
                  <span class="font-normal text-sm">({{ age }})</span>
                  >
                </div>
                <div v-if="prescription.beneficiary?.information.name" class="min-w-64">
                  Ayant droit de {{ prescription.customer.information?.name || 'Anonyme' }}
                </div>
                <div>
                  Dr. {{ prescription.professional?.last_name || 'Anonyme' }},
                  {{ $filters.date(prescription.created_at, 'short') }}
                </div>
              </div>
              <div class="flex flex-1 flex-wrap gap-4">
                <PatientFilterChip
                  v-for="tag in prescription.tags"
                  :key="tag.label"
                  :selected="true"
                  :label="tag.label"
                  :logo="tag.logo"
                  selected-color="white"
                  class="!border-primary !border"
                />
              </div>
            </div>
            <div v-if="prescription.products.length" class="w-full p-3 bg-primary/10">
              <ul class="truncate">
                <li v-for="(product, index) in prescription.products" :key="index">
                  <span class="text-sm"><Icon name="mdi-pill" :size="10" class="text-primary" /> {{
                    product.name
                  }}</span>
                </li>
              </ul>
            </div>
          </div>
          <div class="flex flex-col gap-4 items-end justify-end pt-10">
            <!-- Temporary hide the button until back is merged -->
            <!--            <InteractionAnalysisButton-->
            <!--              :loading="false"-->
            <!--              :consumer-key="`c_${prescription.customer.id}`"-->
            <!--              :product-ids="prescription.products.map(p => p.id)"-->
            <!--            />-->
            <prescription-renewal-button :prescription="prescription" />
            <CustomerCommunicationDialog v-if="prescription.customer" :customer="prescription.customer">
              <BaseButton>
                <div class="flex flex-row items-center gap-2">
                  Envoyer un SMS
                  <SendHorizontal class="w-4" />
                </div>
              </BaseButton>
            </CustomerCommunicationDialog>
            <div class="flex flex-row justify-end">
              <prescription-analysis-button v-if="hasPosos" :prescription="prescription" />
            </div>
          </div>
        </div>
      </div>
    </base-card-body>
    <div
      v-if="prescription.renewal_plan"
      class="flex bg-slate-100 rounded-b-lg py-3 px-6 justify-between"
    >
      <div>
        <div>Prochains renouvellements : <span class="text-primary">{{ renewalDates }}</span></div>
        <div>Expiration: <span class="text-primary">{{ expiresAt }}</span></div>
      </div>
    </div>
  </base-card>
</template>

<script lang="ts" setup>
import {computed, PropType} from "vue";
import SecurePrescriptionPages from '@/core/components/secure-image/SecurePrescriptionPages.vue';
import BaseCardBody from '@/core/components/base/BaseCardBody.vue';
import BaseCard from '@/core/components/base/BaseCard.vue';
import PrescriptionRenewalButton from '@/prescription/components/PrescriptionRenewalButton.vue';
import {Prescription} from "@/prescription/interfaces/Prescription";
import dayjs from "dayjs";
import Icon from "@/core/components/icon/Icon.vue";
import PrescriptionAnalysisButton from "@/prescription/components/PrescriptionAnalysisButton.vue";
import useEnvironment from "@/notification/composables/useEnvironment";
import {ExtensionSlug} from "@/core/interfaces/extension/Extension";
import PatientFilterChip from "@/prescription/components/PatientFilterChip.vue";
import BaseButton from "@/core/components/base/BaseButton.vue";
import {SendHorizontal} from "@lucide/vue";
import CustomerCommunicationDialog from "@/customer/components/customer-communication/CustomerCommunicationDialog.vue";

const {environment} = useEnvironment();

const hasPosos: boolean = !!environment.value?.extensions?.find(extension => extension.slug === ExtensionSlug.POSOS);

const props = defineProps({
    prescription: {
        type: Object as PropType<Prescription>,
        required: true
    }
});

/**
 * Compute the renewal date labels.
 */
const renewalDates = computed(() => {
    const dates = props.prescription.renewal_plan.renewal_dates?.map(renewal => {
        const date = dayjs(renewal.renew_at);

        return dayjs().isBefore(date) ? new Date(renewal.renew_at).toLocaleDateString() : null;
    })
        .filter(value => !!value);

    return dates && dates.length > 0 ? dates.join(', ') : "terminé";
});

const age = computed(() => {
    const birthDate = props.prescription.beneficiary?.information.birth_date
        || props.prescription.customer?.information.birth_date || null;

    if (!birthDate) {
        return 'age inconnu';
    }

    const age = dayjs().diff(birthDate, 'year');

    return `${age} ans`;
});

const expiresAt = computed(() => new Date(props.prescription.renewal_plan.end_at).toLocaleDateString());
</script>
